import React, { useCallback, useMemo, useState } from "react";
import { useIntl, defineMessages } from "react-intl";
import { Divider, ListItem, TextField, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useFormStyles } from "./formFields";
import { getLocalizedNameWithIntl } from "../../globals/langHelpers";

import FormField from "./formFields";
import EditIcon from "../common/icons/editIcon";
import FormSelect from "./formSelect";

import useActivityQuestions from "../activityQuestions/hooks/useActivityQuestions";
import useTranslatedValue from "../../hooks/useTranslatedValue";
import { getQuestionComponentByKey } from "../activityQuestions/components/questions/QuestionComponentMap";
import SingleColumnContainer from "../activityQuestions/components/followupGridContainer/signleColumnContainer";
import { customOrderQuestionsWithDependsOn } from "../../utils/customQuestionsOrderWithDependsOn";
import { processQuestionVisibilityAndMandatory } from "../activityQuestions/utils/mandatoryQuestions";

const messages = defineMessages({
    followupReasonHeader: {
        id: "admin.work-order-details.follow-up-list.reason-col-header",
        defaultMessage: "Follow-up reason",
        description: "Admin work order follow-up reason column header",
    },
    followupCommentHeader: {
        id: "admin.work-order-details.follow-up-list.comment-col-header",
        defaultMessage: "Comments",
        description: "Admin work order follow-up comment column header",
    },

    doneButtonText: {
        id: "task-incomplete-form.done-button-label",
        defaultMessage: "Done",
        description: "Done Button label",
    },
});

export const followUpUseStyles = makeStyles((theme) => ({
    formResult: {
        padding: "13px 0px",
        paddingRight: "13px",
    },
    formActionBtns: {
        margin: "auto 0 auto",
        display: "flex",
        padding: "0px 13px 0px 13px",
        position: "absolute",
        top: "40px",
        right: "0px",
    },
    formDelete: {
        marginTop: "8px",
    },
    formEdit: {
        paddingLeft: "5px",
        margin: "auto",
    },
    followUpReason: {
        fontWeight: "bold",
    },
    followUpSubreason: {
        padding: "3px 0px 2px 0px",
    },
    folowUpComment: {
        color: "#999999",
    },
    followUpHeading: {
        width: "5863px",
        height: "2665px",
        background: "#DBEBF4",
    },
    commentText: {
        color: "#000",
        opacity: 0.5,
        wordBreak: "normal",
    },
}));

export const FollowUpEntryFormField = (props) => {
    const { followUp, index, onReasonChange, reasons: reasonsProp } = props;
    const classes = Object.assign(followUpUseStyles(), useFormStyles());
    const intl = useIntl();
    const getTranslatedValue = useTranslatedValue();

    const {
        activityQuestions: { questions },
    } = useActivityQuestions();
    const followUpQuestions = questions[followUp.uuid]?.mappedQuestions;

    // we are using this property to force refresh the form-fields when a question is answered.
    const [key, setKey] = useState(0);

    const orderedQuestions = useMemo(() => customOrderQuestionsWithDependsOn(followUpQuestions ?? []), [followUpQuestions]);

    const processedQuestions = useMemo(() => {
        return orderedQuestions
            .map((question) => {
                const { isVisible, isMandatory } = processQuestionVisibilityAndMandatory(question, orderedQuestions);
                return {
                    question,
                    isVisible,
                    isMandatory,
                };
            })
            .filter((processed) => processed.isVisible);
    }, [orderedQuestions]);

    const handleReasonChange = useCallback(
        (reason) => {
            onReasonChange(reason);
        },
        [onReasonChange],
    );

    const selectedReason = useMemo(() => {
        return reasonsProp.find((r) => r.followUpTypeID === followUp.id);
    }, [reasonsProp, followUp.id]);

    return (
        <>
            <ListItem>
                <div className={classes.formElement}>
                    <div className={classes.formInfo} style={{ width: "100%" }}>
                        <div className={classes.infoHeader}>{getLocalizedNameWithIntl(intl, { object: followUp, value: "name" })}</div>
                        <div className={classes.formResult}>
                            <FormField
                                field={`followUps.${index}.followupReason`}
                                label={intl.formatMessage(messages.followupReasonHeader)}
                                subtitle={getLocalizedNameWithIntl(intl, {
                                    object: selectedReason,
                                    value: "name",
                                })}
                                editorProps={{
                                    icon: <EditIcon />,
                                    title: intl.formatMessage(messages.followupReasonHeader),
                                    btnText: intl.formatMessage(messages.doneButtonText),
                                    editor: (props) => (
                                        <FormSelect
                                            options={reasonsProp}
                                            existingValue={followUp?.id ? selectedReason : null}
                                            onSelect={(reason) => handleReasonChange(reason)}
                                            {...props}
                                        />
                                    ),
                                }}
                            />
                            {followUp.hasActivityQuestions &&
                                processedQuestions?.length > 0 &&
                                processedQuestions.map(({ question, isMandatory }, i) => (
                                    <div className={classes.folowUpComment} key={`${question.id}-${key}`}>
                                        <FormField
                                            field={`followUps.${index}.${question.title}`}
                                            label={getTranslatedValue(question.questionText)}
                                            divider={processedQuestions?.length - 1 === i ? false : true}
                                            subtitle={getTranslatedValue(question.formattedValue)}
                                            editorProps={{
                                                icon: <EditIcon />,
                                                title: getTranslatedValue(question.questionText),
                                                btnText: intl.formatMessage(messages.doneButtonText),
                                                questionType: question.questionType,
                                                btnCallback: () => setKey((prev) => prev + 1),
                                                editor: () =>
                                                    getQuestionComponentByKey(
                                                        question.questionType,
                                                        { question: { ...question, mandatory: isMandatory }, followUpUUID: followUp.uuid },
                                                        SingleColumnContainer,
                                                    ),
                                            }}
                                            characterCountEnabled
                                        />
                                    </div>
                                ))}
                            <div className={classes.folowUpComment}>
                                <FormField
                                    field={`followUps.${index}.comments`}
                                    label={intl.formatMessage(messages.followupCommentHeader)}
                                    divider={!!processedQuestions?.length}
                                    subtitle={followUp.comments}
                                    editorProps={{
                                        icon: <EditIcon />,
                                        title: intl.formatMessage(messages.followupCommentHeader),
                                        btnText: intl.formatMessage(messages.doneButtonText),
                                        editor: (props) => (
                                            <>
                                                <Typography variant="body1" className={classes.commentText}>
                                                    {intl.formatMessage(messages.followupCommentHeader)}
                                                </Typography>
                                                <TextField
                                                    value={""}
                                                    id="followUp-comments"
                                                    fullWidth
                                                    rows={4}
                                                    variant="outlined"
                                                    InputProps={{
                                                        inputProps: { maxLength: 300 },
                                                    }}
                                                    {...props}
                                                />
                                            </>
                                        ),
                                    }}
                                    characterCountEnabled
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </ListItem>
            <Divider />
        </>
    );
};

export default FollowUpEntryFormField;
